'use client';

import type {
  NavigationItem,
  NavigationGroup,
  NavigationLeaf
} from '@/components/Header/links';
import NavFeatured from '@/components/Header/components/NavFeatured';
import Link from 'next/link';
import {
  AccordionRoot,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@baseline/ui';
import styles from './mobileMenuLink.module.scss';
import { cx } from 'class-variance-authority';

type MobileMenuLinkProps = NavigationItem & { onClick?: () => void };

const MobileMenuLinkSection = ({
  group,
  onClick
}: {
  group: NavigationGroup;
  onClick?: () => void;
}) => {
  return (
    <div className={styles.sectionsWrap}>
      {(group.sections ?? []).map((section, idx, arr) => (
        <div className={styles.section} key={section.label}>
          {section.label && (
            <div className={styles.sectionLabel}>{section.label}</div>
          )}
          <nav>
            {section.items?.map((item, index) => (
              <Link
                onClick={onClick}
                key={item.href ?? item.title}
                className={
                  index === section.items.length - 1
                    ? cx(styles.sublink, styles.removeSublinkPadding)
                    : styles.sublink
                }
                href={item.href}
                target={item.newTab ? '_blank' : undefined}
                rel={item.newTab ? 'noopener noreferrer' : undefined}
              >
                <span className={styles.sublinkTitle}>{item.title}</span>
                {item.description ? (
                  <span className={styles.sublinkDescription}>
                    {item.description}
                  </span>
                ) : null}
              </Link>
            ))}
          </nav>
          {idx < arr.length - 1 ? (
            <div className={styles.sectionDivider} />
          ) : null}
        </div>
      ))}
    </div>
  );
};

export default function MobileMenuLink(props: MobileMenuLinkProps) {
  if ('href' in props) {
    const leaf = props as NavigationLeaf;
    return (
      <Link
        onClick={props.onClick}
        className={`${styles.mobileMenuLink} ${styles.groupTitle}`}
        href={leaf.href}
        target={leaf.newTab ? '_blank' : undefined}
        rel={leaf.newTab ? 'noopener noreferrer' : undefined}
      >
        {leaf.title}
      </Link>
    );
  }
  const group = props as NavigationGroup;
  return (
    <AccordionRoot type="single" collapsible>
      <AccordionItem className={styles.accordionItem} value={group.title}>
        <AccordionTrigger className={styles.mobileMenuLink} icon="chevron">
          <div className={styles.groupHeader}>
            <div className={styles.groupTitle}>{group.title}</div>
          </div>
        </AccordionTrigger>
        <AccordionContent className={styles.sublinks}>
          {Array.isArray(group.sections) ? (
            <MobileMenuLinkSection group={group} onClick={props.onClick} />
          ) : null}
          {group.feature ? (
            <div className={styles.featured}>
              <NavFeatured
                resource={group.feature}
                onNavigate={props.onClick}
              />
            </div>
          ) : null}
        </AccordionContent>
      </AccordionItem>
    </AccordionRoot>
  );
}
